import { featureFlags } from '@seen/core/featureFlags'
import type { ExtendedObservation } from '@seen/core/models/observation'
import { cn } from '@seen/ui'
import { type JSX, useContext, useState } from 'react'
import { toast } from 'sonner'
import { ObservationNoteFieldForm } from '#src/components/rich-text-editor/ObservationNode/ObservationNoteFieldForm.tsx'
import { ObservationNoteSlot } from '#src/components/rich-text-editor/ObservationNode/ObservationNoteSlot.tsx'
import { ObservationRichTextEditor } from '#src/components/rich-text-editor/ObservationNode/ObservationRichTextEditor.tsx'
import {
  type NodeRecord,
  NodeRecordContext,
} from '#src/components/rich-text-editor/ObservationNode/useNodeRecord.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useDebouncedCallback } from '#src/hooks/useDebouncedCallback.ts'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'

// A vital's own note, after its value. The value line is the vital's first
// note, so this one is the clinician note (see `clinicianNote`). Before the
// vital is recorded it is held here and handed to `onPendingChange`, for the
// record to be created with it. Behind the structured capture flag.
export function ObservationVitalNote({
  observation,
  onPendingChange,
  disabled,
}: {
  observation: ExtendedObservation | undefined
  onPendingChange: (note: string) => void
  disabled?: boolean
}): JSX.Element | null {
  const inlineCapture = useFeatureFlag(featureFlags.structuredCapture)
  const [pending, setPending] = useState('')
  // Outside a node (a story) there is no record; the note then saves itself.
  const record = useContext(NodeRecordContext)
  if (!inlineCapture) {
    return null
  }
  const shift = {
    className: cn(
      'grow', // So that the editor can grow to fill the available horizontal space.
      '-ml-[0.1875rem]' // For visual alignment with the spacer below.
    ),
  }
  return (
    <ObservationNoteSlot
      note={observation ? observation.clinicianNote : pending}
    >
      &nbsp;&nbsp;
      <div
        className="mr-[0.1875rem]" // Accounts for the shift below.
      />
      {observation && record?.isDraft ? (
        <DraftClinicianNote
          record={record}
          observation={observation}
          {...shift}
        />
      ) : observation ? (
        <ObservationNoteFieldForm
          observation={observation}
          clinician
          singleLine
          {...shift}
        />
      ) : (
        <ObservationRichTextEditor
          placeholder="Notes…"
          singleLine
          disabled={disabled}
          onChange={(note: string) => {
            setPending(note)
            onPendingChange(note)
          }}
          {...shift}
        />
      )}
    </ObservationNoteSlot>
  )
}

// The clinician note of an answer held in the note: it has no record to patch,
// so the change goes to the node's draft.
function DraftClinicianNote({
  record,
  observation,
  className,
}: {
  record: NodeRecord
  observation: ExtendedObservation
  className: string
}): JSX.Element {
  const practitioner = useCurrentPractitioner()
  const save = useDebouncedCallback((note: string) => {
    record
      .patch((o) => {
        o.setClinicianNote(note || undefined, practitioner.reference)
        o.performerPractitioner = practitioner.reference
      })
      .catch((cause: unknown) => {
        toast.error(
          `Failed to change the note${cause instanceof Error ? `: ${cause.message}` : ''}`
        )
      })
  }, 500)
  return (
    <ObservationRichTextEditor
      placeholder="Notes…"
      singleLine
      value={observation.clinicianNote ?? ''}
      onChange={save}
      onBlur={save.flush}
      className={className}
    />
  )
}
